import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import type { ErrorResponse } from '@/api/handlers/responses';
import { ImportHistorySkeleton } from './ImportHistorySkeleton';
import { ImportHistoryView } from './ImportHistoryView';
import type { ImportHistoryEntry } from './importHistoryMeta';
import { useImportHistory } from './useImportHistory';

type ImportHistoryContentProps = {
    error: ErrorResponse | null;
    isLoading: boolean;
    items: ImportHistoryEntry[];
};

/**
 * Self-served Import-History widget. Same throw-in-child container
 * pattern as the other invoice widgets so the page just drops it in.
 */
export const ImportHistory: React.FC = () => {
    const { data, error, isLoading, onRetry } = useImportHistory();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('purchases.widgets.importHistory.errorTitle'),
            errorMessage: t('purchases.widgets.importHistory.errorMessage'),
            errorRetry: t('purchases.widgets.importHistory.errorRetry'),
        }),
        [t],
    );

    return (
        <ErrorBoundary
            data-testid="import-history-error-boundary"
            fallbackMessage={copy.errorMessage}
            id="import-history-error-boundary"
            onRetry={onRetry}
            resetKeys={[error]}
            retryLabel={copy.errorRetry}
            title={copy.errorTitle}
        >
            <ImportHistoryContent
                error={error}
                isLoading={isLoading}
                items={data}
            />
        </ErrorBoundary>
    );
};

const ImportHistoryContent: React.FC<ImportHistoryContentProps> = ({
    error,
    isLoading,
    items,
}) => {
    if (error && items.length === 0) {
        throw error;
    }

    if (isLoading && items.length === 0) {
        return <ImportHistorySkeleton />;
    }

    return <ImportHistoryView items={items} />;
};
